import React, {useState, useRef, useEffect} from 'react';
import {connect} from 'react-redux';
import S from 'StyledCashoutForm.js';
import {Field, reduxForm, SubmissionError}  from 'redux-form';
import {translation, formatMoney} from 'helpers/utilsHelper.js';
import Box from 'react-styled-box';
import useToggle from 'UseToggle.js';
import ModalOpacity from 'ModalOpacity.js';
import Loader from 'Loader.js';
import _get from 'lodash/get';
import _capitalize from 'lodash/capitalize';

// Validation
import validation from 'sb-validate-js/validate.js';
import validators from 'validators.enum.js';

const validate = values => {
    if (values.maximumAmount && values.maximumAmount.amount) {
        values.maximumAmount = values.maximumAmount.amount;
    }
    const errors = validation(values, validators.CASHOUT);
    return errors;
};

const renderCheckbox = ({
    input,
    label
}) => {
    return (
        <S.AcceptCheckbox as={Box} flexDirection="row" justifyContent="center" alignItems="flex-start">
             <label htmlFor="customerAcceptedAmountChange">{label}</label>
            <input {...input} id="customerAcceptedAmountChange" value={input.value} checked={input.value} type="checkbox" 
                onChange={event => {
                    input.onChange(event.target.checked);
                }}/>
        </S.AcceptCheckbox>
    )
}

const renderField = ({
    input,
    extraData,
    label,
    type,
    disabled,
    customError,
    meta: {touched, error, warning}
}) => {
    if (input.name == 'maximumAmount') {
        input.value = extraData.amount ? extraData.amount : extraData;
    }
    return (
        <S.Row>

            <S.Label>{label}</S.Label>

            <S.InputWrapper>
                <S.Input {...input} type={type} disabled={disabled} required="required"/>
                {touched ?
                    ((error && <S.Error>{error[0]}</S.Error>) || (warning && <S.Error>{warning}</S.Error>))
                    :
                    (customError && <S.Error>{customError}</S.Error>)
                }
            </S.InputWrapper>

        </S.Row>
    )
};

let CashoutForm = ({
    toggleLiveCurtainForTxId,
    curtainActive,
    refreshBetHistoryList,
    dispatch,
    maxAmount,
    prepareCashout,
    cachoutOnSubmit,
    toggleCashoutModal,
    stake,
    possibleReturn,
    currencyCode,
    transactionId,
    combinationId,
    handleSubmit,
    pristine,
    submitting,
    valid,
    error,
    touch
}) => {
    const [showInfoModal, toggleInfoModal] = useToggle(false);
    const [showAmountChangeChbox, toggleAmountChangeChbox] =  useState(false);
    const [infoModalSuccessMsg, setInfoModalSuccessMsg] = useState('');
    const abortController = useRef(new AbortController());
    const cashoutTaxStartingValue = 2280.0;

    useEffect(() => {
        // poprawic, tylko dla curtainActive = true
        return () => {
                abortController.current.abort();
                toggleLiveCurtainForTxId(transactionId, false);
            }
    }, []);


    const onFormSubmit = async(type, values) => {
        let requestedAmount = maxAmount.amount || maxAmount;
        if (type !== "full") {
            requestedAmount = _get(values, 'partialAmount');
        }

        const config = {
            slipId: transactionId,
            requestedAmount,
            countDownRequired: maxAmount.countDownRequired || false,
            type: `cashout${_capitalize(type)}Amount`,
            ...(combinationId && { combinationId })
        };

        const customerAcceptedAmountChange = _get(values, 'customerAcceptedAmountChange');
        if (customerAcceptedAmountChange) {
            config['customerAcceptedAmountChange'] = customerAcceptedAmountChange;
        }

        try {
            const { currentCashoutAmount = null } = await cachoutOnSubmit(config, abortController.current.signal);

            if (type !== "full" && currentCashoutAmount) {
                config['requestedAmount'] = currentCashoutAmount;
            }
            
            const successMsg = `${translation(`account_cashout_cashoutSuccess_${type}`)} ${config.requestedAmount} ${currencyCode}`;

            setInfoModalSuccessMsg(successMsg);
            toggleInfoModal();

        } catch ({message, oddsHasChanged = null}) {
            if (oddsHasChanged) { 
                toggleAmountChangeChbox(true);
            } else {
                toggleAmountChangeChbox(false)
            }

            toggleLiveCurtainForTxId(transactionId, false);
            throw new SubmissionError({_error: message});
        }

    };

    const closeAllModal = async() => {
        toggleCashoutModal();
        try{
            await dispatch(refreshBetHistoryList());
        }catch(error){}
    };

    return (
        <S.CashoutForm method="post">

            {curtainActive && (
                <S.Overlay>
                    <Loader size="5"/>
                </S.Overlay>
            )}

            <ModalOpacity isOpen={showInfoModal} showHeaderIcon={false} showHeaderTitle={false}
                          toggleOpen={closeAllModal} padding="8px">

                <Box flexDirection="column" width="100%" margin="-44px 0 0">
                    <Box justifyContent="center" margin="0 0 20px">
                        <S.ConclusionMsg>{infoModalSuccessMsg}</S.ConclusionMsg>
                    </Box>
                    <Box justifyContent="center">
                        <S.Button onClick={closeAllModal}>{translation('common_ok')}</S.Button>
                    </Box>
                </Box>

            </ModalOpacity>

            <Box flexDirection="column" padding="8px">

                {error && <S.SubmissionError className="submission-error">{translation(`account_${error}`)}</S.SubmissionError>}
                <Box flexDirection="row" justifyContent="space-between" alignItems="center" padding="20px 0 0 0">
                    <S.InfoContainer className="transactionInfoContainer">
                        <div>{translation('account_bettingHistory_cashout')}</div>
                        <div className="close" onClick={toggleCashoutModal}></div>
                    </S.InfoContainer>
                </Box>

                <Box flexDirection="row" justifyContent="space-between" alignItems="center" height={50}>
                    <S.Label>
                        <span>{translation('account_cashout_amountTitle')}</span>
                    </S.Label>

                    <S.Stake>
                        <span>{formatMoney(stake)}</span>&nbsp;
                        <span>{currencyCode}</span>
                    </S.Stake>
                </Box>

                <Box flexDirection="row" margin='0' className="full-cashout">

                    <Box margin='0 20px 0 0' flexGrow={1}>
                        <Field
                            name="maximumAmount"
                            type="text"
                            component={renderField}
                            extraData={maxAmount}
                            label={translation('account_cashout_fullTitle')}
                            disabled={true}
                        />
                    </Box>

                    <Box>
                        <S.SubmitBtn type="button" onClick={handleSubmit(onFormSubmit.bind(null, 'full'))}
                                     disabled={submitting}>{translation('account_cashout_full')}</S.SubmitBtn>
                    </Box>

                </Box>

                <Box flexDirection="row" justifyContent="space-between" alignItems="center" height={60}>
                    <span>{translation('account_cashout_possibleWinTitle')}</span>

                    <S.PossibleReturn>
                        <span>{formatMoney(possibleReturn)}</span>&nbsp;
                        <span>{currencyCode}</span>
                    </S.PossibleReturn>
                </Box>

                <Box flexDirection="row" justifyContent="space-between">
                    <S.Button onClick={toggleCashoutModal} disabled={submitting}>{translation('common_cancel')}</S.Button>&nbsp;
                    <S.Button
                        onClick={prepareCashout.bind(null, transactionId, combinationId)} disabled={submitting}>{translation('common_refresh')}</S.Button>
                </Box>
 
                {(maxAmount?.amount ?? 0) > cashoutTaxStartingValue && <S.TaxInfoMessage> {translation('cashoutForm_taxInfo')} </S.TaxInfoMessage> }

                {showAmountChangeChbox && (
                    <Box flexDirection="row" justifyContent="flex-end" alignItems="center" height={60}>
                      <Field
                            name="customerAcceptedAmountChange"
                            component={renderCheckbox}
                            label={translation('account_cashout_acceptAmountChange')}
                            required={false}
                        />
                    </Box>)
                }

            </Box>

        </S.CashoutForm>
    )
};

CashoutForm = reduxForm({
    form: 'cashoutForm',
    validate
})(CashoutForm);

export default CashoutForm;

